import { useEffect, useRef, useState } from 'react';
import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import { RuntimeTerminalView, prepareRuntimeTerminalOutput, type RuntimeTerminalOutputFrame } from '../workbench/RuntimeTerminalView';
import type { DesignCapabilityRendererContext } from './contracts';

export const RUNTIME_TERMINAL_MODEL_SCHEMA: JsonObject = {
  type: 'object', additionalProperties: false,
  properties: { statusLabelKey: { type: 'string', minLength: 1, maxLength: 256 }, disabled: { type: 'boolean' } },
};
interface TerminalContext extends DesignCapabilityRendererContext {
  stream?: { sessionKey: string; status: string; model?: JsonObject; events: readonly JsonObject[]; errorCode?: string };
  sendStreamControl?: (port: 'terminal.input' | 'terminal.resize' | 'stream.disconnect', payload: JsonObject) => unknown;
}
const safeCode = (code: unknown) => typeof code === 'string' && /^[A-Za-z][A-Za-z0-9_.-]{0,127}$/.test(code) ? code : 'PAGE_STREAM_UNAVAILABLE';
function TerminalSession({ context }: { context: TerminalContext }) {
  const { stream, model, translate } = context;
  const latest = useRef(context); latest.current = context;
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const [outputError, setOutputError] = useState<string>();
  const dimensions = useRef<{ columns: number; rows: number }>();
  const sentDimensions = useRef<string>();
  const sessionKey = stream!.sessionKey;
  const disabled = model.disabled === true || stream!.status !== 'open' || Boolean(outputError);
  let output: RuntimeTerminalOutputFrame[] = [], invalid = false;
  const initial = stream!.model?.output;
  try {
    if (initial !== undefined && !Array.isArray(initial)) throw new TypeError('Invalid output');
    const values = [...(Array.isArray(initial) ? initial : []), ...stream!.events];
    output = values.map(value => {
      if (!value || typeof value !== 'object' || Array.isArray(value) || !Number.isSafeInteger(value.sequence) || Number(value.sequence) < 1 || typeof value.data !== 'string') throw new TypeError('Invalid output');
      return { sequence: Number(value.sequence), data: value.data };
    });
    prepareRuntimeTerminalOutput({ highWater: 0, bytes: 0, frames: [] }, output);
  } catch { invalid = true; output = []; }
  const send = (key: string, port: 'terminal.input' | 'terminal.resize', payload: JsonObject) => {
    const current = latest.current;
    if (!active.current || key !== current.stream?.sessionKey || current.stream.status !== 'open' || current.model.disabled === true || outputError || invalid || !current.sendStreamControl) return;
    try { void Promise.resolve(current.sendStreamControl(port, payload)).catch(() => { if (active.current && latest.current.stream?.sessionKey === key) setOutputError('PAGE_STREAM_CONTROL_FAILED'); }); }
    catch { setOutputError('PAGE_STREAM_CONTROL_FAILED'); }
  };
  const error = invalid ? 'PAGE_STREAM_OUTPUT_INVALID' : outputError ?? stream!.errorCode;
  const flushResize = () => {
    const value = dimensions.current;
    if (!value || disabled || invalid || !latest.current.sendStreamControl) return;
    const identity = `${value.columns}:${value.rows}`;
    if (sentDimensions.current === identity) return;
    sentDimensions.current = identity;
    send(sessionKey, 'terminal.resize', value);
  };
  useEffect(() => {
    if (disabled || invalid) { sentDimensions.current = undefined; return; }
    flushResize();
  }, [stream!.status, model.disabled, outputError, invalid, context.sendStreamControl]);
  return <div data-monkeys-component="runtime-terminal-capability">
    {error ? <div role="alert">{safeCode(error)}</div> : null}
    <RuntimeTerminalView sessionKey={sessionKey} output={output} disabled={disabled || invalid}
      statusLabel={translate(typeof model.statusLabelKey === 'string' ? model.statusLabelKey : 'monkeys.design.capability.runtimeTerminal.status')}
      onInput={event => send(event.sessionKey, 'terminal.input', { data: event.data })}
      onResize={event => {
        if (!active.current || event.sessionKey !== latest.current.stream?.sessionKey || !Number.isInteger(event.columns) || !Number.isInteger(event.rows) || event.columns < 1 || event.rows < 1) return;
        dimensions.current = { columns: event.columns, rows: event.rows };
        flushResize();
      }}
      onOutputError={event => { if (active.current && latest.current.stream?.sessionKey === event.sessionKey) setOutputError(safeCode(event.code)); }} />
  </div>;
}
export function RuntimeTerminalCapabilityRenderer(context: TerminalContext) {
  if (!context.stream?.sessionKey) return <div role="status">{context.translate('monkeys.design.capability.runtimeTerminal.waiting')}</div>;
  return <TerminalSession key={context.stream.sessionKey} context={context} />;
}
